import { FC, memo, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled, { css } from 'styled-components';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { LayoutConst } from 'core/modules';
import { CreateLinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { ModerationType, isOuterCourseTeam } from "../../pages/teams/const/ModerationTypes";
import { ModuleFormat } from "../../constants/ModuleFormat";
import { ProjectCardTitle } from "../projectCard/styles";
import { LMSCardFormatTypes, LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetLMSImage } from 'shared/hooks/api/useGetLMSImage';
import { LikesBox } from '../likesBox';
import { useProfile } from '@brg/auth';
import { ExternalTeamBadge } from '../TeamBadges/ExternalTeamBadge';
import { CourseStatusBadge } from '../TeamBadges/CourseStatusBadge';
import { SubscribeTools } from '../Subscribe/SubscribeTools';
import { SUBSCRIBE_STATUS, SUBSCRIBE_VISIBILITY_STATUS } from '../Subscribe/const';
import { DesignerTab } from 'shared/constants/designerTab';
import { HiddenHolder, StopAndHiddenHolder, StopHolder } from '../SubscribeHolders/SubscribeHolders';
import { SubscriptionsTeamBadge } from '../TeamBadges/SubscriptionsTeamBadge';
import { DRAFT_TEAM_STATUS, isPersonalOrDefaultChannel, PUBLISHED_TEAM_STATUS, REJECTED_TEAM_STATUS, UNDER_APPROVAL_TEAM_STATUS } from 'shared/helpers/teamsStatus';
import { ProjectCardTools } from '../projectCard/ProjectCardTools';
import urlApi from 'shared/actions/urlApi';

/** 
* Показать конкурсные баллы 
* @param showCompetitionPoint 
*/

const SelectRing = styled(FlexBox) <{ active?: boolean }>`
  background: rgba(171, 174, 175, 0.85);
  border: 2px solid #FFF;
  box-sizing: border-box;
  border-radius: 34px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  color: white;

  @media(max-width: ${LayoutConst.screen.md}px) {
    z-index: 5;
    width: 24px;
    height: 24px;
  }

  ${p => p.active ? css`
    background: ${Colors.geoWhite100};
    border: 2px solid ${Colors.geoWhite100};
    box-sizing: border-box;
    border-radius: 34px;
  ` : null}
`;

export const LmsTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
`;

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`


const Subscribers = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  font-size: 11px;
  line-height: 13px;
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`


export interface TeamCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  disableHover?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  isMe?: boolean;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any, openInMedia?: boolean) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;
  onUpdate?: any;
  team?: ComputedTeam;
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
  linkTarget?: string;
  /**
   * Принудительно показывать название карточки под обложкой на всех разрешениях
   * (без вывода названия поверх обложки на десктопе). Используется в ленте FeedsApp.
   */
  forceTitleBelow?: boolean;
  isOuterOrganizationShowcase?: boolean;
  isProfile?: boolean;
}

const TeamCard: FC<TeamCardProps> = (props) => {
  const {
    isLoading,
    isSelectable,
    isDeletable,
    isRemovableFromBook,
    isEditable,
    isRejected,
    tooltip,
    isMe,
    width,
    height,
    disableHover,
    onApprove,
    onReject,
    onSelect,
    onShouldReload,
    onTeamClick,
    onRemoveFromBook,
    team,
    contextName,
    linkPrefix,
    linkTo,
    linkTarget,
    entityType,
    forceTitleBelow,
    isOuterOrganizationShowcase,
    isProfile,
    onUpdate,
    ...handlers
  } = props;
  const profile = useProfile();
  const { t, i18n } = useTranslation();
  const isOnlines = AppTester.isOnlines();
  const isMedia = AppTester.isMediiia();
  const isMobile = useIsMobile();
  const isHsePermCourses = !!useRouteMatch('/teams/hsepermcourses')
  const isTimeLine = !!useRouteMatch('/timeline');
  

  const likes = team?.quCount || 0;
  const isLiked = team?.isQu || false;

  const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const LMSTitleImage = useGetLMSImage(team);

  const isLMSModerationType = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  // const { modules: modulesRaw } = useGetModulesList(isLMSModerationType ? team?.teamId : '')

  const handleToolTipToggleMove = (val: boolean) => {
    setTooltipOpen && setTooltipOpen(val);
  }

  const LMSImageFiles = useMemo(() => {
    //@ts-ignore
    return (LMSTitleImage?.length > 0 && LMSTitleImage[0]?.files?.length > 0 && LMSTitleImage[0]?.files) || []
  }, [LMSTitleImage, team])

  const titleImages = getProjectImages(team, i18n.language);

  const isLMSCourseWithParent = isLMSModerationType && !!team?.parentTeamId;

  const handleTeamClick = (projectId?: string, e?: any) => {
    if (!isLMSCourseWithParent && team?.lmsModulesIds?.length === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(CreateLinkTo.project(team?.lmsModulesIds?.[0] || ''), '_blank');
    } else {
      team && onTeamClick && onTeamClick(team, e);
    }

  }

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS && !isHsePermCourses, [team?.moderationType, isHsePermCourses])

  const isUnitCourse = useMemo(() => {
    //@ts-ignore
    return team?.contexts?.includes(contextName)
  }, [contextName, team])

  const showLMSCardType = isLMS && team?.moderationType === ModerationType.LMS;

  const lmsCardType = useMemo(() => {
    if (showLMSCardType) {
      return (team?.lmsCover !== LMSCardFormatTypes.short) ||
        (entityType && entityType !== "organizations")
        ? "t-shape"
        : undefined;
    }
    if (isPersonalOrDefaultChannel(team, profile) && entityType === DesignerTab.subscriptionsTeams) {
      return 'fixed-ratio-small'
    }
    return undefined;
  }, [team, isUnitCourse, profile, showLMSCardType, entityType]);

  const horizontalCover = useMemo(() => {
    //@ts-ignore
    return ((isLMS || isUnitCourse) && team?.lmsCourseTerm === LMSCourseDurationTypes.short)
  }, [isLMS, team, isUnitCourse])
  // @ts-ignore
  const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])

  const badge = useMemo(() => {
    const hasProfile = profile;
    const isExternalTeam = team?.moderationType === ModerationType.Outer;
    const isOwner = team?.ownersIDs?.includes(profile?.accountId ?? '')

    if (!(hasProfile || isExternalTeam || isOwner || team)) return null;

    return <ExternalTeamBadge status={team?.status} />
  } , [profile, team])

  const title = useMemo(() => {

    if (isTimeLine || disableHover) return null;

    if (isLMS) {
      // @ts-ignore
      const newDate = new Date(team?.lmsModuleStartDate || null);
      const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      const hasStartDate = Boolean(team?.lmsModuleStartDate) && !isNaN(newDate.getTime());
      const startText = hasStartDate ? `Старт ${newDate.getDate()} ${months[newDate.getMonth()]}` : 'В любое время';
      const format = team?.lmsModuleFormat;
      // @ts-ignore
      const price = (team?.lmsModulePrice || 0) + ' ₽';

      // @ts-ignore
      return <ProjectCardTitle><LmsTitle>{team?.name}<br />{format === ModuleFormat.PreRecorded ? 'Записанный' : format === ModuleFormat.Online ? 'Онлайн' : 'Очная форма обучения'} | {startText}{team?.lmsModulePrice ? ` | ${price}` : ''}</LmsTitle></ProjectCardTitle>

    }
    //@ts-ignore
    else if (team?.coverTitle?.length) {
      //@ts-ignore
      return <ProjectCardTitle>{team?.coverTitle}</ProjectCardTitle>
    }
    else if (team?.displayName?.length) {
      return <ProjectCardTitle>{team?.displayName}</ProjectCardTitle>
    }
    else if (team?.name?.length) {
      return (
      <ProjectCardTitle>
        {team?.name}
      </ProjectCardTitle>)
    } else return null;

  }, [team, isLMS, isUnitCourse, isTimeLine])

  const [subscribeStatusLocal, setSubscribeStatusLocal] = useState<SUBSCRIBE_STATUS>(
    ((team as any)?.subscribeStatus ?? SUBSCRIBE_STATUS.active)
  );
  const [visibilityLocal, setVisibilityLocal] = useState<SUBSCRIBE_VISIBILITY_STATUS>(
    ((team as any)?.visibility ?? SUBSCRIBE_VISIBILITY_STATUS.visible)
  );
  
  useEffect(() => {
    setSubscribeStatusLocal((team as any)?.subscribeStatus ?? SUBSCRIBE_STATUS.active);
    setVisibilityLocal((team as any)?.visibility ?? SUBSCRIBE_VISIBILITY_STATUS.visible);
  }, [team]);

  const subscribeTools = useMemo(() => {
    if (!isMe) return null;
    if (entityType !== DesignerTab.subscriptionsTeams || !team?.teamId) return undefined;
    const subscriptionHandlers = {
      ...handlers,
      onUnsubscribe: (subscriberId: string, targetId: string, targetType?: string) => {
        setSubscribeStatusLocal(SUBSCRIBE_STATUS.disable);
        // @ts-ignore
        handlers?.onUnsubscribe?.(subscriberId, targetId, targetType);
      },
      onEditSubscribe: (subscriberId?: string, targetType?: string) => {
        setSubscribeStatusLocal(SUBSCRIBE_STATUS.active);
        // @ts-ignore
        handlers?.onEditSubscribe?.(subscriberId, targetType);
      },
      onUpdateSubVisibility: (subscriberId: string, targetId: string, mode: SUBSCRIBE_VISIBILITY_STATUS, targetType?: string) => {
        setVisibilityLocal(mode);
        // @ts-ignore
        handlers?.onUpdateSubVisibility?.(subscriberId, targetId, mode, targetType);
      },
    };

    return (
      <SubscribeTools
        {...subscriptionHandlers}
        identifier={team.teamId}
        targetType="team"
        name={team?.name}
        status={subscribeStatusLocal ?? SUBSCRIBE_STATUS.active}
        visibility={visibilityLocal ?? SUBSCRIBE_VISIBILITY_STATUS.visible}
        
      />
    );
  }, [entityType, isMe, team, subscribeStatusLocal, visibilityLocal])

  const likeComponent = useMemo(() => {
    const color = isMedia && isMobile ? Colors.galBlack100 : Colors.geoWhite100
    if (team?.showQu) {
      return <LikesBox likes={likes} isLiked={isLiked} isInteractive={false} />
    } else if (team?.subscribersCount && team?.subscribersCount > 0) {
      return (
        <Subscribers style={{ color }}>{t('team_view.subscribers', { count: team?.subscribersCount })}</Subscribers>
      )
    }
    return null;
  }, [team?.showQu, likes, isLiked])

  const isTeamSubscriptionTab = entityType === DesignerTab.subscriptionsTeams;
  const isStopSubscribe = isTeamSubscriptionTab && subscribeStatusLocal === SUBSCRIBE_STATUS.disable;
  const isHiddenSubscribe = isTeamSubscriptionTab && visibilityLocal === SUBSCRIBE_VISIBILITY_STATUS.hidden;

  const teamMore = useMemo(() => {
    const { status, teamId } = team || {}
    const isDeletable = !!(status && status === DRAFT_TEAM_STATUS)
    const isEditable = !!(status && [DRAFT_TEAM_STATUS, UNDER_APPROVAL_TEAM_STATUS, REJECTED_TEAM_STATUS].includes(status))
    const isCanBeDraft = !!(status && [UNDER_APPROVAL_TEAM_STATUS, REJECTED_TEAM_STATUS].includes(status))

    if (!(isEditable || isDeletable || isCanBeDraft)) return <></>

    return <ProjectCardTools
        tooltipOpen={true}
        toggleTooltip={() => false}
        placement='bottom-start'
        isDeletable={isDeletable}
        isEditable={isEditable}
        isCanBeDraft={isCanBeDraft}
        onDelete={() => {
          if (teamId) {
            const confirmation = window.confirm(t('team_view.delete_draft_confirm'))
            if (confirmation) {
              urlApi().onUpdateTeamStatus(teamId, 1).then(res => onUpdate())
            }
          }
        }}
        onMoveDrafts={() => teamId && urlApi().onUpdateTeamStatus(teamId, 2).then(res => onUpdate())}
        onEdit={() => teamId && window.open(CreateLinkTo.teamExternalEdit(teamId), '_self')}
      />
  }, [team])

  return (
    <ProjectCardLayoutNew
      title={title}
      coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
      identifier={team?.teamId}
      onClick={handleTeamClick}
      titleImages={(isLMS && LMSImageFiles?.length > 0) ? LMSImageFiles : titleImages}
      isLoading={isLoading}
      width={width}
      team={team}
      
      hasMoreTeamCard={!!badge && isProfile}      
      badge={badge}
      courseStatusBadge={isOuterCourseTeam(team)
        ? <CourseStatusBadge lmsCourseStatus={team?.lmsCourseStatus} />
        : undefined}
      subscriptionsUpdatedText={entityType === DesignerTab.subscriptionsTeams && team && (
        <SubscriptionsTeamBadge date={team?.contentUpdate}/>
      )}
      height={height}
      isMoreVisible={true}
      isRejected={isRejected}
      metaAlwaysVisible={false}
      tooltipOpen={isTooltipOpen}
      toggleTooltip={handleToolTipToggleMove}
      linkPrefix={linkPrefix ? linkPrefix : undefined}
      needLmsCover={horizontalCover}
      linkTo={linkTo}
      linkTarget={linkTarget}
      projectType={lmsCardType}
      lmsCardType={lmsCardType}
      withoutGoToProject={!linkTo}
      author={
        isLMS ? teamOwner : !isOnlines && (
          <Author>
            <span style={{ color: isMedia && isMobile ? '#151514' : '#fefefe' }}>{team?.name}</span>
          </Author>
        )
      }
      likes={likeComponent}
      more={teamMore}
      isTeamSubscription={isTeamSubscriptionTab}
      forceTitleBelow={forceTitleBelow}
      coverLowOpacity={isHiddenSubscribe || isStopSubscribe}
      coverOverlay={isHiddenSubscribe && isStopSubscribe ? 
        <StopAndHiddenHolder /> 
        : isHiddenSubscribe
        ? <HiddenHolder subtitleKey="subscription.hidden_subscribe_subtitle_team" />
        : isStopSubscribe
        ? <StopHolder subtitleKey="subscription.stopped_subscribe_subtitle_team" />
        : undefined}
      isTeam
    />
  );
}
  ;

export default memo(TeamCard);
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled, { css } from 'styled-components';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import { LayoutConst } from 'core/modules';
import { CreateLinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { ModerationType, isOuterCourseTeam } from "../../pages/teams/const/ModerationTypes";
import { ModuleFormat } from "../../constants/ModuleFormat";
import { ProjectCardTitle } from "../projectCard/styles";
import { LMSCardFormatTypes, LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { useGetModulesList } from 'shared/hooks/api/LMS/useGetModulesList';
import { useGetLMSImage } from 'shared/hooks/api/useGetLMSImage';
import { LikesBox } from '../likesBox';
import { useProfile } from '@brg/auth';
import { ExternalTeamBadge } from '../TeamBadges/ExternalTeamBadge';
import { CourseStatusBadge } from '../TeamBadges/CourseStatusBadge';
import { SubscribeTools } from '../Subscribe/SubscribeTools';
import { SUBSCRIBE_STATUS, SUBSCRIBE_VISIBILITY_STATUS } from '../Subscribe/const';
import { DesignerTab } from 'shared/constants/designerTab';
import { HiddenHolder, StopAndHiddenHolder, StopHolder } from '../SubscribeHolders/SubscribeHolders';
import { SubscriptionsTeamBadge } from '../TeamBadges/SubscriptionsTeamBadge';
import { DRAFT_TEAM_STATUS, isPersonalOrDefaultChannel, PUBLISHED_TEAM_STATUS, REJECTED_TEAM_STATUS, UNDER_APPROVAL_TEAM_STATUS } from 'shared/helpers/teamsStatus';
import { ProjectCardTools } from '../projectCard/ProjectCardTools';
import urlApi from 'shared/actions/urlApi';

/** 
* Показать конкурсные баллы 
* @param showCompetitionPoint 
*/

const SelectRing = styled(FlexBox) <{ active?: boolean }>`
  background: rgba(171, 174, 175, 0.85);
  border: 2px solid #FFF;
  box-sizing: border-box;
  border-radius: 34px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  color: white;

  @media(max-width: ${LayoutConst.screen.md}px) {
    z-index: 5;
    width: 24px;
    height: 24px;
  }

  ${p => p.active ? css`
    background: ${Colors.geoWhite100};
    border: 2px solid ${Colors.geoWhite100};
    box-sizing: border-box;
    border-radius: 34px;
  ` : null}
`;

export const LmsTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
`;

const Author = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`


const Subscribers = styled(FInter.SmallText)`
  color: ${Colors.geoWhite47};
  font-size: 11px;
  line-height: 13px;
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`


export interface TeamCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  disableHover?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  isMe?: boolean;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any, openInMedia?: boolean) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;
  onUpdate?: any;
  team?: ComputedTeam;
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
  linkTarget?: string;
  /**
   * Принудительно показывать название карточки под обложкой на всех разрешениях
   * (без вывода названия поверх обложки на десктопе). Используется в ленте FeedsApp.
   */
  forceTitleBelow?: boolean;
  isOuterOrganizationShowcase?: boolean;
  isProfile?: boolean;
}

const TeamCard: FC<TeamCardProps> = (props) => {
  const {
    isLoading,
    isSelectable,
    isDeletable,
    isRemovableFromBook,
    isEditable,
    isRejected,
    tooltip,
    isMe,
    width,
    height,
    disableHover,
    onApprove,
    onReject,
    onSelect,
    onShouldReload,
    onTeamClick,
    onRemoveFromBook,
    team,
    contextName,
    linkPrefix,
    linkTo,
    linkTarget,
    entityType,
    forceTitleBelow,
    isOuterOrganizationShowcase,
    isProfile,
    onUpdate,
    ...handlers
  } = props;
  const profile = useProfile();
  const { t, i18n } = useTranslation();
  const isOnlines = AppTester.isOnlines();
  const isMedia = AppTester.isMediiia();
  const isMobile = useIsMobile();
  const isHsePermCourses = !!useRouteMatch('/teams/hsepermcourses')
  const isTimeLine = !!useRouteMatch('/timeline');
  

  const likes = team?.quCount || 0;
  const isLiked = team?.isQu || false;

  const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const LMSTitleImage = useGetLMSImage(team);

  const isLMSModerationType = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  // const { modules: modulesRaw } = useGetModulesList(isLMSModerationType ? team?.teamId : '')

  const handleToolTipToggleMove = (val: boolean) => {
    setTooltipOpen && setTooltipOpen(val);
  }

  const LMSImageFiles = useMemo(() => {
    //@ts-ignore
    return (LMSTitleImage?.length > 0 && LMSTitleImage[0]?.files?.length > 0 && LMSTitleImage[0]?.files) || []
  }, [LMSTitleImage, team])

  const titleImages = getProjectImages(team, i18n.language);

  const isLMSCourseWithParent = isLMSModerationType && !!team?.parentTeamId;

  const handleTeamClick = (projectId?: string, e?: any) => {
    if (!isLMSCourseWithParent && team?.lmsModulesIds?.length === 1) {
      e.preventDefault();
      e.stopPropagation();
      window.open(CreateLinkTo.project(team?.lmsModulesIds?.[0] || ''), '_blank');
    } else {
      team && onTeamClick && onTeamClick(team, e);
    }

  }

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS && !isHsePermCourses, [team?.moderationType, isHsePermCourses])

  const isUnitCourse = useMemo(() => {
    //@ts-ignore
    return team?.contexts?.includes(contextName)
  }, [contextName, team])

  const showLMSCardType = isLMS && team?.moderationType === ModerationType.LMS;

  const lmsCardType = useMemo(() => {
    if (showLMSCardType) {
      return (team?.lmsCover !== LMSCardFormatTypes.short) ||
        (entityType && entityType !== "organizations")
        ? "t-shape"
        : undefined;
    }
    if (isPersonalOrDefaultChannel(team, profile) && entityType === DesignerTab.subscriptionsTeams) {
      return 'fixed-ratio-small'
    }
    return undefined;
  }, [team, isUnitCourse, profile, showLMSCardType, entityType]);

  const horizontalCover = useMemo(() => {
    //@ts-ignore
    return ((isLMS || isUnitCourse) && team?.lmsCourseTerm === LMSCourseDurationTypes.short)
  }, [isLMS, team, isUnitCourse])
  // @ts-ignore
  const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])

  const badge = useMemo(() => {
    const hasProfile = profile;
    const isExternalTeam = team?.moderationType === ModerationType.Outer;
    const isOwner = team?.ownersIDs?.includes(profile?.accountId ?? '')

    if (!(hasProfile || isExternalTeam || isOwner || team)) return null;

    return <ExternalTeamBadge status={team?.status} />
  } , [profile, team])

  const title = useMemo(() => {

    if (isTimeLine || disableHover) return null;

    if (isLMS) {
      // @ts-ignore
      const newDate = new Date(team?.lmsModuleStartDate || null);
      const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      const hasStartDate = Boolean(team?.lmsModuleStartDate) && !isNaN(newDate.getTime());
      const startText = hasStartDate ? `Старт ${newDate.getDate()} ${months[newDate.getMonth()]}` : 'В любое время';
      const format = team?.lmsModuleFormat;
      // @ts-ignore
      const price = (team?.lmsModulePrice || 0) + ' ₽';

      // @ts-ignore
      return <ProjectCardTitle><LmsTitle>{team?.name}<br />{format === ModuleFormat.PreRecorded ? 'Записанный' : format === ModuleFormat.Online ? 'Онлайн' : 'Очная форма обучения'} | {startText}{team?.lmsModulePrice ? ` | ${price}` : ''}</LmsTitle></ProjectCardTitle>

    }
    //@ts-ignore
    else if (team?.coverTitle?.length) {
      //@ts-ignore
      return <ProjectCardTitle>{team?.coverTitle}</ProjectCardTitle>
    }
    else if (team?.displayName?.length) {
      return <ProjectCardTitle>{team?.displayName}</ProjectCardTitle>
    }
    else if (team?.name?.length) {
      return (
      <ProjectCardTitle>
        {team?.name}
      </ProjectCardTitle>)
    } else return null;

  }, [team, isLMS, isUnitCourse, isTimeLine])

  const [subscribeStatusLocal, setSubscribeStatusLocal] = useState<SUBSCRIBE_STATUS>(
    ((team as any)?.subscribeStatus ?? SUBSCRIBE_STATUS.active)
  );
  const [visibilityLocal, setVisibilityLocal] = useState<SUBSCRIBE_VISIBILITY_STATUS>(
    ((team as any)?.visibility ?? SUBSCRIBE_VISIBILITY_STATUS.visible)
  );
  
  useEffect(() => {
    setSubscribeStatusLocal((team as any)?.subscribeStatus ?? SUBSCRIBE_STATUS.active);
    setVisibilityLocal((team as any)?.visibility ?? SUBSCRIBE_VISIBILITY_STATUS.visible);
  }, [team]);

  const subscribeTools = useMemo(() => {
    if (!isMe) return null;
    if (entityType !== DesignerTab.subscriptionsTeams || !team?.teamId) return undefined;
    const subscriptionHandlers = {
      ...handlers,
      onUnsubscribe: (subscriberId: string, targetId: string, targetType?: string) => {
        setSubscribeStatusLocal(SUBSCRIBE_STATUS.disable);
        // @ts-ignore
        handlers?.onUnsubscribe?.(subscriberId, targetId, targetType);
      },
      onEditSubscribe: (subscriberId?: string, targetType?: string) => {
        setSubscribeStatusLocal(SUBSCRIBE_STATUS.active);
        // @ts-ignore
        handlers?.onEditSubscribe?.(subscriberId, targetType);
      },
      onUpdateSubVisibility: (subscriberId: string, targetId: string, mode: SUBSCRIBE_VISIBILITY_STATUS, targetType?: string) => {
        setVisibilityLocal(mode);
        // @ts-ignore
        handlers?.onUpdateSubVisibility?.(subscriberId, targetId, mode, targetType);
      },
    };

    return (
      <SubscribeTools
        {...subscriptionHandlers}
        identifier={team.teamId}
        targetType="team"
        name={team?.name}
        status={subscribeStatusLocal ?? SUBSCRIBE_STATUS.active}
        visibility={visibilityLocal ?? SUBSCRIBE_VISIBILITY_STATUS.visible}
        
      />
    );
  }, [entityType, isMe, team, subscribeStatusLocal, visibilityLocal])

  const likeComponent = useMemo(() => {
    const color = isMedia && isMobile ? Colors.galBlack100 : Colors.geoWhite100
    if (team?.showQu) {
      return <LikesBox likes={likes} isLiked={isLiked} isInteractive={false} />
    } else if (team?.subscribersCount && team?.subscribersCount > 0) {
      return (
        <Subscribers style={{ color }}>{t('team_view.subscribers', { count: team?.subscribersCount })}</Subscribers>
      )
    }
    return null;
  }, [team?.showQu, likes, isLiked])

  const isTeamSubscriptionTab = entityType === DesignerTab.subscriptionsTeams;
  const isStopSubscribe = isTeamSubscriptionTab && subscribeStatusLocal === SUBSCRIBE_STATUS.disable;
  const isHiddenSubscribe = isTeamSubscriptionTab && visibilityLocal === SUBSCRIBE_VISIBILITY_STATUS.hidden;

  const teamMore = useMemo(() => {
    const { status, teamId } = team || {}
    const isDeletable = !!(status && status === DRAFT_TEAM_STATUS)
    const isEditable = !!(status && [DRAFT_TEAM_STATUS, UNDER_APPROVAL_TEAM_STATUS, REJECTED_TEAM_STATUS].includes(status))
    const isCanBeDraft = !!(status && [UNDER_APPROVAL_TEAM_STATUS, REJECTED_TEAM_STATUS].includes(status))

    if (!(isEditable || isDeletable || isCanBeDraft)) return <></>

    return <ProjectCardTools
        tooltipOpen={true}
        toggleTooltip={() => false}
        placement='bottom-start'
        isDeletable={isDeletable}
        isEditable={isEditable}
        isCanBeDraft={isCanBeDraft}
        onDelete={() => {
          if (teamId) {
            const confirmation = window.confirm(t('team_view.delete_draft_confirm'))
            if (confirmation) {
              urlApi().onUpdateTeamStatus(teamId, 1).then(res => onUpdate())
            }
          }
        }}
        onMoveDrafts={() => teamId && urlApi().onUpdateTeamStatus(teamId, 2).then(res => onUpdate())}
        onEdit={() => teamId && window.open(CreateLinkTo.teamExternalEdit(teamId), '_self')}
      />
  }, [team])

  return (
    <ProjectCardLayoutNew
      title={title}
      coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
      identifier={team?.teamId}
      onClick={handleTeamClick}
      titleImages={(isLMS && LMSImageFiles?.length > 0) ? LMSImageFiles : titleImages}
      isLoading={isLoading}
      width={width}
      team={team}
      
      hasMoreTeamCard={!!badge && isProfile}      
      badge={badge}
      courseStatusBadge={isOuterCourseTeam(team)
        ? <CourseStatusBadge lmsCourseStatus={team?.lmsCourseStatus} />
        : undefined}
      subscriptionsUpdatedText={entityType === DesignerTab.subscriptionsTeams && team && (
        <SubscriptionsTeamBadge date={team?.contentUpdate}/>
      )}
      height={height}
      isMoreVisible={true}
      isRejected={isRejected}
      metaAlwaysVisible={false}
      tooltipOpen={isTooltipOpen}
      toggleTooltip={handleToolTipToggleMove}
      linkPrefix={linkPrefix ? linkPrefix : undefined}
      needLmsCover={horizontalCover}
      linkTo={linkTo}
      linkTarget={linkTarget}
      projectType={lmsCardType}
      lmsCardType={lmsCardType}
      withoutGoToProject={!linkTo}
      author={
        isLMS ? teamOwner : !isOnlines && (
          <Author>
            <span style={{ color: isMedia && isMobile ? '#151514' : '#fefefe' }}>{team?.name}</span>
          </Author>
        )
      }
      likes={likeComponent}
      more={teamMore}
      isTeamSubscription={isTeamSubscriptionTab}
      forceTitleBelow={forceTitleBelow}
      coverLowOpacity={isHiddenSubscribe || isStopSubscribe}
      coverOverlay={isHiddenSubscribe && isStopSubscribe ? 
        <StopAndHiddenHolder /> 
        : isHiddenSubscribe
        ? <HiddenHolder subtitleKey="subscription.hidden_subscribe_subtitle_team" />
        : isStopSubscribe
        ? <StopHolder subtitleKey="subscription.stopped_subscribe_subtitle_team" />
        : undefined}
      isTeam
    />
  );
}
  ;

export default memo(TeamCard);
